/**
 * Recommendations page — Google Ads suggestions enriched with our AI verdict.
 *
 * Layered design:
 *   Layer 1 — compact card (label + impact summary + verdict pill)
 *   Layer 2 — expanded details (AI reason, base vs potential metrics, deep link)
 *   Layer 3 — bulk actions (filter by category/verdict, dismiss-all)
 *
 * Unified card component handles all ~30 recommendation types — they share the
 * same response schema; only the icon and the verdict text differ.
 *
 * Backend contract (see backend/server.py /api/recommendations):
 *   { recommendations: [...], counts: {...}, fetched_at, ai_enriched_at, not_connected }
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Sparkles, RefreshCw, Loader2, ExternalLink, X,
    KeyRound, Megaphone, TrendingUp, Wallet, Activity, MoreHorizontal,
    CheckCircle2, HelpCircle, XCircle, ChevronDown, ChevronUp,
    AlertCircle, Lightbulb,
} from "lucide-react";
import { toast } from "sonner";
import AppShell from "@/components/nav/AppShell";
import RecommendationsLoadingState from "@/components/RecommendationsLoadingState";
import { api } from "@/lib/api";

// ─── Category presentation ──────────────────────────────────────────────────
// Maps our 6 backend categories to icon + colour + UA label. Anything else
// falls back to "other".
const CATEGORIES = {
    keywords: { Icon: KeyRound,        label: "Ключові слова", color: "text-primary-c",  bg: "bg-primary/10" },
    ads:      { Icon: Megaphone,       label: "Оголошення",    color: "text-primary-c",     bg: "bg-primary/10" },
    bidding:  { Icon: TrendingUp,      label: "Ставки",        color: "text-success-c",  bg: "bg-success/10" },
    budget:   { Icon: Wallet,          label: "Бюджет",        color: "text-warning-c",  bg: "bg-warning/10" },
    tracking: { Icon: Activity,        label: "Трекінг",       color: "text-purple-400", bg: "bg-purple-500/10" },
    other:    { Icon: MoreHorizontal,  label: "Інше",          color: "text-zinc-400",   bg: "bg-zinc-500/10" },
};

// ─── Verdict presentation ───────────────────────────────────────────────────
// Order matters — APPLY first, then MAYBE, SKIP. Used for sorting too.
const VERDICTS = {
    apply: { Icon: CheckCircle2, label: "Варто застосувати", color: "text-success-c",  bg: "bg-success/10",  border: "border-success/30",  rank: 0 },
    maybe: { Icon: HelpCircle,   label: "На розгляд",        color: "text-warning-c",  bg: "bg-warning/10",  border: "border-warning/30",  rank: 1 },
    skip:  { Icon: XCircle,      label: "Можна пропустити",  color: "text-zinc-400",   bg: "bg-zinc-500/10", border: "border-zinc-500/20", rank: 2 },
};

const cat = (key) => CATEGORIES[key] || CATEGORIES.other;
const ver = (key) => VERDICTS[key] || VERDICTS.maybe;

const formatRelative = (iso) => {
    if (!iso) return "—";
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60)    return "щойно";
    if (diff < 3600)  return `${Math.floor(diff / 60)} хв тому`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} год тому`;
    return `${Math.floor(diff / 86400)} дн тому`;
};

const formatMoney = (n) => `€${(n || 0).toLocaleString("uk-UA", { maximumFractionDigits: 0 })}`;
const formatInt   = (n) => (n || 0).toLocaleString("uk-UA");

// Deep link to Google Ads recommendations for the customer.
// We can't apply via API (Explorer Access) so user clicks through.
const googleAdsLink = (customerId) =>
    customerId
        ? `https://ads.google.com/aw/recommendations?ocid=${customerId}`
        : "https://ads.google.com/aw/recommendations";


// ════════════════════════════════════════════════════════════════════════════
// SINGLE RECOMMENDATION CARD — universal, handles all types
// ════════════════════════════════════════════════════════════════════════════
const RecommendationCard = ({ rec, customerId, onDismiss }) => {
    const [expanded, setExpanded] = useState(false);
    const [dismissing, setDismissing] = useState(false);
    const c = cat(rec.category);
    const v = ver(rec.ai_verdict);

    // Highlight metric: prefer potential conversions, fall back to clicks, then cost saved.
    const potential = rec.impact?.potential || {};
    const base = rec.impact?.base || {};
    const headlineMetric = useMemo(() => {
        if (potential.conversions > 0)
            return { label: `+${potential.conversions.toFixed(1)} конверсій`, positive: true };
        if (potential.clicks > base.clicks)
            return { label: `+${formatInt(potential.clicks - base.clicks)} кліків`, positive: true };
        if (base.cost > potential.cost && (base.cost - potential.cost) > 0)
            return { label: `Економія ${formatMoney(base.cost - potential.cost)}`, positive: true };
        return null;
    }, [base, potential]);

    const handleDismiss = async (e) => {
        e.stopPropagation();
        if (dismissing) return;
        setDismissing(true);
        try {
            // resource_name like "customers/123/recommendations/456" — encode for URL path.
            const encoded = encodeURIComponent(rec.resource_name);
            await api.post(`/recommendations/${encoded}/dismiss`);
            onDismiss(rec.resource_name);
            toast.success("Рекомендацію приховано");
        } catch {
            toast.error("Не вдалося приховати");
            setDismissing(false);
        }
    };

    return (
        <div className={`rounded-2xl border ${v.border} ${expanded ? "bg-surface-hover/30" : "bg-surface hover:bg-surface-hover/30"} transition-colors`}>
            {/* ── Layer 1: compact row ────────────────────────────────────── */}
            <button
                onClick={() => setExpanded(!expanded)}
                className="w-full text-left p-4 flex items-start gap-3"
            >
                {/* Category icon */}
                <div className={`shrink-0 w-10 h-10 rounded-xl ${c.bg} ${c.color} flex items-center justify-center`}>
                    <c.Icon className="w-5 h-5" />
                </div>

                {/* Main content */}
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-text-primary">{rec.label}</span>
                        <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${v.bg} ${v.color}`}>
                            <v.Icon className="w-3 h-3" />
                            {v.label}
                        </span>
                    </div>
                    {headlineMetric && (
                        <div className={`text-xs mt-1 ${headlineMetric.positive ? "text-success-c" : "text-faint-c"}`}>
                            {headlineMetric.label}
                        </div>
                    )}
                    {rec.ai_reason && !expanded && (
                        <div className="text-xs text-faint-c mt-1 line-clamp-1">{rec.ai_reason}</div>
                    )}
                </div>

                {/* Expand chevron */}
                <div className="shrink-0 text-faint-c">
                    {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </div>
            </button>

            {/* ── Layer 2: expanded details ───────────────────────────────── */}
            {expanded && (
                <div className="px-4 pb-4 pt-1 space-y-3 border-t border-border">
                    {/* AI reasoning */}
                    {rec.ai_reason ? (
                        <div className={`rounded-lg p-3 ${v.bg} flex gap-2`}>
                            <Lightbulb className={`w-4 h-4 mt-0.5 shrink-0 ${v.color}`} />
                            <div className="text-sm text-secondary-c">{rec.ai_reason}</div>
                        </div>
                    ) : (
                        <div className="rounded-lg p-3 bg-surface-hover/60 text-xs text-faint-c flex gap-2">
                            <AlertCircle className="w-4 h-4 shrink-0" />
                            AI-аналіз тимчасово недоступний для цієї рекомендації.
                        </div>
                    )}

                    {/* Impact comparison */}
                    {(rec.impact?.base || rec.impact?.potential) && (
                        <div>
                            <div className="text-xs text-faint-c mb-2 uppercase tracking-wide">Прогноз від Google</div>
                            <div className="grid grid-cols-2 gap-2 text-xs">
                                <ImpactCol title="Зараз" data={base} />
                                <ImpactCol title="Після застосування" data={potential} highlight />
                            </div>
                        </div>
                    )}

                    {/* Actions */}
                    <div className="flex flex-wrap gap-2 pt-1">
                        <a
                            href={googleAdsLink(customerId)}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-primary text-background hover:bg-primary/90 transition-colors"
                        >
                            <ExternalLink className="w-3.5 h-3.5" />
                            Відкрити в Google Ads
                        </a>
                        <button
                            onClick={handleDismiss}
                            disabled={dismissing}
                            className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-surface-hover/60 text-secondary-c hover:bg-surface-hover/60/80 transition-colors disabled:opacity-50"
                        >
                            {dismissing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <X className="w-3.5 h-3.5" />}
                            Приховати
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

const ImpactCol = ({ title, data, highlight }) => (
    <div className={`rounded-lg p-2.5 ${highlight ? "bg-success/5 border border-success/20" : "bg-surface-hover/60"}`}>
        <div className="text-faint-c mb-1.5">{title}</div>
        <div className="space-y-1">
            <Metric label="Покази"      value={formatInt(data?.impressions)} />
            <Metric label="Кліки"       value={formatInt(data?.clicks)} />
            <Metric label="Конверсії"   value={(data?.conversions || 0).toFixed(1)} />
            <Metric label="Витрати"     value={formatMoney(data?.cost)} />
        </div>
    </div>
);
const Metric = ({ label, value }) => (
    <div className="flex items-center justify-between text-secondary-c">
        <span className="text-faint-c">{label}</span>
        <span className="font-medium tabular-nums">{value}</span>
    </div>
);


// ════════════════════════════════════════════════════════════════════════════
// MAIN PAGE
// ════════════════════════════════════════════════════════════════════════════
const Recommendations = () => {
    const navigate = useNavigate();
    const [data, setData] = useState(null);          // {recommendations, counts, ...}
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [filterCategory, setFilterCategory] = useState("all");
    const [filterVerdict, setFilterVerdict] = useState("all");

    const load = useCallback(async () => {
        try {
            const r = await api.get("/recommendations");
            setData(r.data);
        } catch (e) {
            toast.error("Не вдалося завантажити рекомендації");
            setData({ recommendations: [], counts: { total: 0, by_category: {}, by_verdict: {} } });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const handleRefresh = async () => {
        if (refreshing) return;
        setRefreshing(true);
        try {
            const r = await api.post("/recommendations/refresh");
            setData(r.data);
            toast.success("Рекомендації оновлено");
        } catch (e) {
            // 429 → cooldown message from backend
            const msg = e?.response?.data?.detail || "Не вдалося оновити";
            toast.error(msg);
        } finally {
            setRefreshing(false);
        }
    };

    const handleDismiss = (resourceName) => {
        // Optimistic update — remove from list immediately.
        setData((prev) => {
            if (!prev) return prev;
            const next = prev.recommendations.filter((r) => r.resource_name !== resourceName);
            return { ...prev, recommendations: next, counts: { ...prev.counts, total: next.length } };
        });
    };

    // ── Filtering + sorting ────────────────────────────────────────────────
    // We always sort by verdict rank (apply → maybe → skip) so the most
    // actionable items float to the top.
    const visible = useMemo(() => {
        if (!data?.recommendations) return [];
        let list = data.recommendations;
        if (filterCategory !== "all") list = list.filter((r) => r.category === filterCategory);
        if (filterVerdict !== "all")  list = list.filter((r) => r.ai_verdict === filterVerdict);
        return [...list].sort((a, b) => ver(a.ai_verdict).rank - ver(b.ai_verdict).rank);
    }, [data, filterCategory, filterVerdict]);

    // ── States: loading / not connected / empty / list ─────────────────────
    if (loading) {
        // Replaced bare centered spinner with skeleton + rotating status
        // (5-15 sec wait felt dead with just a spinner). See PR #29.
        return (
            <AppShell>
                <RecommendationsLoadingState />
            </AppShell>
        );
    }

    if (data?.not_connected) {
        return (
            <AppShell>
                <PageHeader />
                <EmptyState
                    title="Підключи Google Ads, щоб бачити рекомендації"
                    body="Ми витягуємо рекомендації прямо з твого акаунта і додаємо AI-аналіз під твою стратегію."
                    cta={{ label: "Підключити Google Ads", onClick: () => navigate("/settings") }}
                />
            </AppShell>
        );
    }

    return (
        <AppShell>
            <PageHeader
                counts={data?.counts}
                fetchedAt={data?.fetched_at}
                aiEnrichedAt={data?.ai_enriched_at}
                onRefresh={handleRefresh}
                refreshing={refreshing}
            />

            {/* Filters */}
            <FiltersBar
                counts={data?.counts}
                filterCategory={filterCategory}
                setFilterCategory={setFilterCategory}
                filterVerdict={filterVerdict}
                setFilterVerdict={setFilterVerdict}
            />

            {/* List */}
            {visible.length === 0 ? (
                data?.recommendations?.length === 0 ? (
                    <EmptyState
                        title="Поки що Google не має для тебе рекомендацій"
                        body="Це гарний знак — кампанії запущені та працюють стабільно. Перевіримо знову завтра."
                    />
                ) : (
                    <EmptyState
                        title="Нічого не знайдено за фільтрами"
                        body="Спробуй прибрати фільтри або перевір іншу категорію."
                    />
                )
            ) : (
                <div className="space-y-2 mt-4">
                    {visible.map((rec) => (
                        <RecommendationCard
                            key={rec.resource_name}
                            rec={rec}
                            customerId={data?.customer_id}
                            onDismiss={handleDismiss}
                        />
                    ))}
                </div>
            )}
        </AppShell>
    );
};


// ─── Subcomponents ──────────────────────────────────────────────────────────
const PageHeader = ({ counts, fetchedAt, aiEnrichedAt, onRefresh, refreshing }) => (
    <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div>
            <h1 className="text-2xl font-semibold text-text-primary flex items-center gap-2">
                <Sparkles className="w-6 h-6 text-primary-c" />
                Рекомендації Google
            </h1>
            <p className="text-sm text-faint-c mt-1">
                {counts?.total > 0
                    ? `${counts.total} рекомендацій • AI визначив пріоритети під твою стратегію`
                    : "Витягуємо рекомендації з Google Ads + наш AI оцінює кожну"}
            </p>
            {fetchedAt && (
                <p className="text-xs text-faint-c mt-1">
                    Оновлено: {formatRelative(fetchedAt)}
                    {aiEnrichedAt && " • AI-аналіз свіжий"}
                </p>
            )}
        </div>
        {onRefresh && (
            <button
                onClick={onRefresh}
                disabled={refreshing}
                className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded-lg bg-surface-hover/30 hover:bg-surface-hover/60 border border-border text-secondary-c transition-colors disabled:opacity-50"
            >
                {refreshing
                    ? <Loader2 className="w-4 h-4 animate-spin" />
                    : <RefreshCw className="w-4 h-4" />}
                Оновити
            </button>
        )}
    </div>
);

const FiltersBar = ({ counts, filterCategory, setFilterCategory, filterVerdict, setFilterVerdict }) => {
    // Build category chips only for categories that actually have items.
    const categoryEntries = Object.entries(counts?.by_category || {})
        .filter(([, n]) => n > 0);

    if (!counts?.total) return null;

    return (
        <div className="space-y-2">
            {/* Verdict tabs — primary filter */}
            <div className="flex gap-1.5 flex-wrap">
                <VerdictTab active={filterVerdict === "all"}    onClick={() => setFilterVerdict("all")} label="Всі"          count={counts?.total} />
                <VerdictTab active={filterVerdict === "apply"}  onClick={() => setFilterVerdict("apply")} label="Варто"      count={counts?.by_verdict?.apply || 0} verdict="apply" />
                <VerdictTab active={filterVerdict === "maybe"}  onClick={() => setFilterVerdict("maybe")} label="На розгляд" count={counts?.by_verdict?.maybe || 0} verdict="maybe" />
                <VerdictTab active={filterVerdict === "skip"}   onClick={() => setFilterVerdict("skip")}  label="Пропустити" count={counts?.by_verdict?.skip || 0}  verdict="skip" />
            </div>

            {/* Category chips — secondary filter */}
            {categoryEntries.length > 1 && (
                <div className="flex gap-1.5 flex-wrap text-xs">
                    <CategoryChip active={filterCategory === "all"} onClick={() => setFilterCategory("all")} label="Всі категорії" />
                    {categoryEntries.map(([key, n]) => {
                        const c = cat(key);
                        return (
                            <CategoryChip
                                key={key}
                                active={filterCategory === key}
                                onClick={() => setFilterCategory(key)}
                                label={`${c.label} (${n})`}
                                Icon={c.Icon}
                                color={c.color}
                            />
                        );
                    })}
                </div>
            )}
        </div>
    );
};

const VerdictTab = ({ active, onClick, label, count, verdict }) => {
    const v = verdict ? ver(verdict) : null;
    return (
        <button
            onClick={onClick}
            className={`text-xs px-3 py-1.5 rounded-lg border transition-colors ${
                active
                    ? "bg-fg-1 text-bg-1 border-fg-1"
                    : `bg-surface-hover/30 hover:bg-surface-hover/60 text-secondary-c border-border ${v ? v.color : ""}`
            }`}
        >
            {label} <span className="opacity-60">({count})</span>
        </button>
    );
};

const CategoryChip = ({ active, onClick, label, Icon, color }) => (
    <button
        onClick={onClick}
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border transition-colors ${
            active
                ? "bg-surface-hover/60 border-border text-text-primary"
                : `bg-surface-hover/30 hover:bg-surface-hover/60 text-faint-c border-border ${color || ""}`
        }`}
    >
        {Icon && <Icon className="w-3 h-3" />}
        {label}
    </button>
);

const EmptyState = ({ title, body, cta }) => (
    <div className="rounded-2xl border border-border bg-surface-hover/30 p-8 text-center">
        <Sparkles className="w-10 h-10 text-faint-c mx-auto mb-3 opacity-50" />
        <div className="text-text-primary font-medium">{title}</div>
        <div className="text-sm text-faint-c mt-1 max-w-md mx-auto">{body}</div>
        {cta && (
            <button
                onClick={cta.onClick}
                className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-background hover:bg-primary/90 transition-colors text-sm"
            >
                {cta.label}
            </button>
        )}
    </div>
);

export default Recommendations;